import instance from './instance';
import { ResponseDto } from '../dto/ResponseDto';
import { OciAiTestDto } from '../dto/OciAiTestDto';
import { ENV } from '../constants/env';

const PREFIX_PATH = `${ENV.PRE_FIX}/${ENV.ADMIN_PATH}`;

/**
 * OCI AI 테스트 API 클라이언트
 * 
 * @description OCI GenAI 서비스를 활용한 AI 모델 테스트 및 관리 API
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-20
 */

/**
 * AI 모델과 채팅 완성 수행
 * @param data 채팅 완성 요청 데이터
 * @returns 채팅 완성 응답
 */
export const chatCompletion = async (
  data: OciAiTestDto.ChatCompletionRequest
): Promise<ResponseDto<OciAiTestDto.ChatCompletionResponse>> => {
  // instance interceptor에서 이미 response.data를 반환하므로 response 자체가 응답 데이터
  const response = await instance.post<ResponseDto<OciAiTestDto.ChatCompletionResponse>>(
    `${PREFIX_PATH}/oci-ai-test/chat`,
    data
  );
  return response;
};

/**
 * 특정 모델로 채팅 완성 수행
 * @param modelId 사용할 모델 ID
 * @param data 채팅 완성 요청 데이터
 * @returns 채팅 완성 응답
 */
export const chatCompletionWithModel = async (
  modelId: string,
  data: Omit<OciAiTestDto.ChatCompletionRequest, 'modelId'>
): Promise<ResponseDto<OciAiTestDto.ChatCompletionResponse>> => {
  // instance interceptor에서 이미 response.data를 반환하므로 response 자체가 응답 데이터
  const response = await instance.post<ResponseDto<OciAiTestDto.ChatCompletionResponse>>(
    `${PREFIX_PATH}/oci-ai-test/chat/${modelId}`,
    data
  );
  return response;
};

/**
 * 텍스트를 영어로 번역
 * @param data 번역 요청 데이터
 * @returns 번역 응답
 */
export const translate = async (
  data: OciAiTestDto.TranslateRequest
): Promise<ResponseDto<OciAiTestDto.TranslateResponse>> => {
  // instance interceptor에서 이미 response.data를 반환하므로 response 자체가 응답 데이터
  const response = await instance.post<ResponseDto<OciAiTestDto.TranslateResponse>>(
    `${PREFIX_PATH}/oci-ai-test/translate`,
    data
  );
  return response;
};

/**
 * 텍스트의 언어 감지
 * @param data 언어 감지 요청 데이터
 * @returns 언어 감지 응답
 */
export const detectLanguage = async (
  data: OciAiTestDto.DetectLanguageRequest
): Promise<ResponseDto<OciAiTestDto.DetectLanguageResponse>> => {
  // instance interceptor에서 이미 response.data를 반환하므로 response 자체가 응답 데이터
  const response = await instance.post<ResponseDto<OciAiTestDto.DetectLanguageResponse>>(
    `${PREFIX_PATH}/oci-ai-test/detect-language`,
    data
  );
  return response;
};

/**
 * 사용 가능한 AI 모델 목록 조회
 * @returns 모델 목록
 */
export const getAvailableModels = async (): Promise<
  ResponseDto<OciAiTestDto.ModelInfo[]>
> => {
  const apiUrl = `${PREFIX_PATH}/oci-ai-test/models`;
  
  console.log('[API_CALL]', {
    url: apiUrl,
    method: 'GET',
    PREFIX_PATH,
    baseURL: ENV.API_URL,
  });

  try {
    // instance interceptor에서 이미 response.data를 반환하므로 response 자체가 응답 데이터
    const response = await instance.get<ResponseDto<OciAiTestDto.ModelInfo[]>>(
      apiUrl
    );

    console.log('[API_RESPONSE]', {
      url: apiUrl,
      success: response.success,
      message: response.message,
      dataCount: Array.isArray(response.data) ? response.data.length : 0,
    });

    return response;
  } catch (error: any) {
    console.error('[API_ERROR]', {
      api: 'getAvailableModels',
      url: apiUrl,
      error: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    throw error;
  }
};

/**
 * 여러 모델로 동시에 채팅 완성 수행 (모델 비교용)
 * @param modelIds 사용할 모델 ID 배열
 * @param data 채팅 완성 요청 데이터
 * @returns 각 모델별 채팅 완성 응답 배열
 */
export const chatCompletionMultiModel = async (
  modelIds: string[],
  data: Omit<OciAiTestDto.ChatCompletionRequest, 'modelId'>
): Promise<OciAiTestDto.MultiModelResponse[]> => {
  // 병렬로 여러 모델에 요청
  const promises = modelIds.map(async (modelId) => {
    try {
      const startTime = Date.now();
      const response = await chatCompletionWithModel(modelId, data);
      const endTime = Date.now();
      
      return {
        modelId,
        success: response.success,
        data: response.data,
        error: null,
        responseTime: endTime - startTime,
      };
    } catch (error) {
      const endTime = Date.now();
      return {
        modelId,
        success: false,
        data: null,
        error: error instanceof Error ? error.message : 'Unknown error',
        responseTime: endTime - Date.now(),
      };
    }
  });

  return await Promise.all(promises);
};